"use client";
/* eslint id-length: "off", no-magic-numbers: "off", max-statements: ["error", 36] */
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import MDMusicMulti from "@/components/music/md-player/MDMusicMulti";
import MDMusicSingle from "@/components/music/md-player/MDMusicSingle";
import "@/lib/music/fontawesome";

const DEFAULT_VOLUME = 0.8;
const MAX_LOAD_RETRIES = 2;
const MS_PER_SECOND = 1000;

// Pick a source URL the element can actually play.
// Single-source tracks use their only entry.
// Multi-source (md: opus + mp3) defers to canPlayType, so Safari uses mp3.
const pickSource = (track, node) => {
  if (!track.sources?.length) {
    return "";
  }
  if (track.sources.length === 1 || !node?.canPlayType) {
    return track.sources[0].src;
  }
  const supported = track.sources.find(item => node.canPlayType(item.type));
  return (supported ?? track.sources[0]).src;
};

const MDMusicPlayerClient = ({tracks}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [isLoading, setIsLoading] = useState(false);
  // No playlist row is selected until the user engages a track.
  // The detail pane still shows the first track via `currentIndex`.
  const [isActive, setIsActive] = useState(false);
  const audioRef = useRef(null);
  const retriesRef = useRef(0);
  // True while the user drags the seek bar.
  // Playback timeupdates are ignored until release, when we seek once.
  const seekingRef = useRef(false);

  const current = tracks[currentIndex];

  useEffect(() => {
    const node = audioRef.current;
    if (node) {
      node.volume = volume;
    }
  }, [volume]);

  const startPlayback = useCallback(node => {
    const promise = node.play();
    if (promise?.catch) {
      promise.catch(() => setIsPlaying(false));
    }
  }, []);

  // Load and play a track imperatively, within the user-gesture tick.
  // Setting src + play() here keeps Safari/iOS autoplay unlocked.
  // A deferred effect on a remounted element would lose the gesture.
  const playIndex = useCallback(idx => {
    setCurrentIndex(idx);
    setCurrentTime(0);
    setDuration(0);
    setIsActive(true);
    setIsPlaying(true);
    retriesRef.current = 0;
    const node = audioRef.current;
    if (!node) {
      return;
    }
    const track = tracks[idx];
    node.crossOrigin = track.crossOrigin ?? null;
    node.src = pickSource(track, node);
    startPlayback(node);
  }, [tracks, startPlayback]);

  const handleSelect = useCallback(idx => {
    const node = audioRef.current;
    if (isActive && idx === currentIndex) {
      if (node && isPlaying) {
        node.pause();
      } else if (node) {
        setIsPlaying(true);
        startPlayback(node);
      }
      return;
    }
    playIndex(idx);
  }, [isActive, currentIndex, isPlaying, playIndex, startPlayback]);

  const handleToggle = useCallback(() => {
    const node = audioRef.current;
    if (!isActive) {
      playIndex(currentIndex);
      return;
    }
    if (!node) {
      return;
    }
    if (isPlaying) {
      node.pause();
    } else {
      setIsPlaying(true);
      startPlayback(node);
    }
  }, [isActive, currentIndex, isPlaying, playIndex, startPlayback]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      playIndex(currentIndex - 1);
    }
  }, [currentIndex, playIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < tracks.length - 1) {
      playIndex(currentIndex + 1);
    }
  }, [currentIndex, tracks.length, playIndex]);

  const handleEnded = useCallback(() => {
    if (currentIndex < tracks.length - 1) {
      playIndex(currentIndex + 1);
    } else {
      setIsPlaying(false);
    }
  }, [currentIndex, tracks.length, playIndex]);

  // While dragging, only move the displayed position; don't seek the audio yet.
  const handleSeekInput = useCallback(event => {
    seekingRef.current = true;
    setCurrentTime(Number(event.target.value));
  }, []);

  // On release (mouse/touch/key commit), seek the audio to the final position.
  const handleSeekCommit = useCallback(event => {
    const next = Number(event.target.value);
    const node = audioRef.current;
    if (node) {
      node.currentTime = next;
    }
    setCurrentTime(next);
    seekingRef.current = false;
  }, []);

  const handleVolume = useCallback(event => {
    setVolume(Number(event.target.value));
  }, []);

  // Same-origin md streams report Infinity duration and can stall near the end.
  // Reloading would just restart them, so only retry external previews.
  // A transient ORB or network block there is recoverable by re-fetching.
  const handleError = useCallback(() => {
    const node = audioRef.current;
    if (!node) {
      return;
    }
    if (current?.source !== "md" && retriesRef.current < MAX_LOAD_RETRIES) {
      retriesRef.current += 1;
      node.load();
      if (isPlaying) {
        startPlayback(node);
      }
    } else {
      setIsLoading(false);
      setIsPlaying(false);
    }
  }, [current, isPlaying, startPlayback]);

  // Trust the file's own duration only when finite (am/dz previews).
  // Streamed md tracks report Infinity, so use the known API value instead.
  const audioDurationOk = Number.isFinite(duration) && duration > 0;
  const knownDuration = current ? (current.durationMs ?? 0) / MS_PER_SECOND : 0;
  let displayDuration = 0;
  if (audioDurationOk) {
    displayDuration = duration;
  } else if (current?.source === "md") {
    displayDuration = knownDuration;
  }

  const controlProps = useMemo(() => ({
    currentTime,
    duration: displayDuration,
    isLoading,
    isPlaying,
    volume,
    onSeekInput: handleSeekInput,
    onSeekCommit: handleSeekCommit,
    onToggle: handleToggle,
    onVolume: handleVolume,
  }), [
    currentTime,
    displayDuration,
    isLoading,
    isPlaying,
    volume,
    handleSeekInput,
    handleSeekCommit,
    handleToggle,
    handleVolume,
  ]);

  if (!current) {
    return null;
  }

  const transportProps = {
    hasPrev: currentIndex > 0,
    hasNext: currentIndex < tracks.length - 1,
    onPrev: handlePrev,
    onNext: handleNext,
  };
  const activeIndex = isActive ? currentIndex : -1;
  const view = tracks.length === 1
    ? <MDMusicSingle controlProps={controlProps} track={current}/>
    : (
      <MDMusicMulti activeIndex={activeIndex}
                    controlProps={controlProps}
                    track={current}
                    tracks={tracks}
                    transportProps={transportProps}
                    onSelect={handleSelect}
      />
    );

  return (
    <div className="mdmusic-wrapper">
      {view}
      <audio ref={audioRef}
             preload="none"
             style={{display: "none"}}
             onCanPlay={() => setIsLoading(false)}
             onDurationChange={event => setDuration(event.currentTarget.duration || 0)}
             onEnded={handleEnded}
             onError={handleError}
             onLoadedMetadata={event => setDuration(event.currentTarget.duration || 0)}
             onPause={() => setIsPlaying(false)}
             onPlay={() => setIsPlaying(true)}
             onPlaying={() => setIsLoading(false)}
             onTimeUpdate={event => {
               if (!seekingRef.current) {
                 setCurrentTime(event.currentTarget.currentTime);
               }
             }}
             onWaiting={() => setIsLoading(true)}
      />
    </div>
  );
};

MDMusicPlayerClient.propTypes = {
  tracks: PropTypes.array.isRequired,
};

export default MDMusicPlayerClient;
